import moment from 'moment';
import { Match } from '@views/WarehouseDetails/WarehouseDetails';
import {
  BillingCycle,
  Invoice,
  InvoiceLine,
  VAT_RATE,
  additionalServicesOptions,
} from './invoiceTypes';
import {
  InvoiceTableEditSection,
  InvoiceTableEditSectionProps,
} from './InvoiceTableEditSection';
import { getInvoiceLineVatCost } from './invoiceHelper';

export function InvoiceTable({
  billingCycle,
  invoice,
  invoiceLines,
  match,
  subTotalAmount,
  subTotalVatAmount,
  editSectionProps,
}: {
  billingCycle: BillingCycle;
  invoice: Invoice;
  invoiceLines: InvoiceLine[];
  match: Match | undefined;
  subTotalAmount: number;
  subTotalVatAmount: number;
  editSectionProps: InvoiceTableEditSectionProps | null;
}) {
  const startDate = moment(billingCycle.startDate);
  const endDate = moment(billingCycle.endDate);

  const isEditable = !!editSectionProps;

  return (
    <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-gray-200 bg-gray-50'>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16'>
                #
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Service provided: for the period from{' '}
                {startDate.format('DD/MM/YY')} to {endDate.format('DD/MM/YY')}
              </th>
              <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                Amount
              </th>
              <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                VAT @{VAT_RATE}%
              </th>
              <th className='px-4 py-3 w-16'>
                <span className='sr-only'>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {invoiceLines.map((line) => {
              const vatAmount = getInvoiceLineVatCost(line);

              const matchingService = additionalServicesOptions.find(
                (option) => option.value === line.description
              );

              return (
                <tr key={line.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-4 text-sm text-gray-500'>
                    {line.lineNumber}
                  </td>
                  <td className='px-4 py-4 text-sm text-gray-900'>
                    {matchingService ? matchingService.label : line.description}
                  </td>
                  <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
                    {line.currencySymbol}
                    {line.amount.toFixed(2)}
                  </td>
                  <td className='px-4 py-4 text-sm font-medium text-gray-900 text-right'>
                    {line.currencySymbol}
                    {vatAmount.toFixed(2)}
                  </td>
                  <td className='px-4 py-4'>
                    <span className='sr-only' />
                  </td>
                </tr>
              );
            })}
            {isEditable && (
              <InvoiceTableEditSection
                billingCycle={billingCycle}
                invoice={invoice}
                match={match}
                {...editSectionProps}
              />
            )}
            {/* Subtotal Row */}
            <tr className='bg-gray-50'>
              <td className='px-4 py-4'>
                <span className='sr-only'>Line Number</span>
              </td>
              <td className='px-4 py-4 text-right'>
                <span className='text-sm font-medium text-gray-700'>
                  Sub Total
                </span>
              </td>
              <td className='px-4 py-4 text-right'>
                <span className='text-sm font-semibold text-gray-900'>
                  {invoice.lines[0].currencySymbol}
                  {subTotalAmount.toFixed(2)}
                </span>
              </td>
              <td className='px-4 py-4 text-right'>
                <span className='text-sm font-semibold text-gray-900'>
                  {invoice.lines[0].currencySymbol}
                  {subTotalVatAmount.toFixed(2)}
                </span>
              </td>
              <td className='px-4 py-4'>
                <span className='sr-only' />
              </td>
            </tr>
            {/* Grand Total Row */}
            <tr className='bg-green-50'>
              <td className='px-4 py-4'>
                <span className='sr-only'>Line Number</span>
              </td>
              <td className='px-4 py-4 text-right'>
                <span className='text-sm font-medium text-green-800'>
                  Grand Total
                </span>
              </td>
              <td className='px-4 py-4 text-right' colSpan={2}>
                <span className='text-base font-bold text-green-800'>
                  {invoice.lines[0].currencySymbol}
                  {(subTotalAmount + subTotalVatAmount).toFixed(2)}
                </span>
              </td>
              <td className='px-4 py-4'>
                <span className='sr-only' />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
